import React, { useState, useRef, useEffect } from 'react';
import {
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  MapPin,
  Lock,
  Users,
  Eye,
  Download,
  BarChart3,
  CalendarDays,
  Ban,
  FileSpreadsheet,
  FileText,
  History,
} from 'lucide-react';
import {
  useScheduledSessions,
  useAttendanceStats,
} from '../../hooks/useProfessor';
import {
  exportAttendanceExcel,
  exportAttendancePdf,
} from '../../services/professorService';
import type {
  ScheduledSessionInfo,
  StudentAttendanceStats,
} from '@/types/professor.types';
import AttendanceModal from './AttendanceModal';

interface AttendanceTabProps {
  groupeId: string;
  moduleElementId: string;
  // Year being consulted; omitted means the live one.
  academicYearId?: string;
}

type ViewMode = 'sessions' | 'students';

const AttendanceTab: React.FC<AttendanceTabProps> = ({
  groupeId,
  moduleElementId,
  academicYearId,
}) => {
  const [selectedSession, setSelectedSession] = useState<ScheduledSessionInfo | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('sessions');
  const [showFutureSessions, setShowFutureSessions] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Export functions
  const handleExportExcel = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const blob = await exportAttendanceExcel(groupeId, moduleElementId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presence_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export Excel:', error);
      alert('Erreur lors de l\'export Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const blob = await exportAttendancePdf(groupeId, moduleElementId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presence_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Erreur lors de l\'export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch scheduled sessions
  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    refetch: refetchSessions
  } = useScheduledSessions(groupeId, moduleElementId, academicYearId);

  // Fetch attendance stats
  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useAttendanceStats(groupeId, moduleElementId, academicYearId);

  // Handle modal close and refresh
  const handleModalSaved = () => {
    refetchSessions();
    refetchStats();
  };

  // Format date display
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Format short date
  const formatShortDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  // Get attendance status badge
  const getAttendanceStatusBadge = (status: 'not_filled' | 'partial' | 'complete' | 'cancelled') => {
    switch (status) {
      case 'complete':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
            <CheckCircle className="h-3 w-3" />
            Complétée
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
            <AlertTriangle className="h-3 w-3" />
            Partielle
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            <Ban className="h-3 w-3" />
            Annulée
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            <Clock className="h-3 w-3" />
            Non remplie
          </span>
        );
    }
  };

  // Get student initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // Get avatar color based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500',
      'bg-rose-500',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const isLoading = sessionsLoading || statsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto" />
          <p className="mt-3 text-sm text-gray-500">Chargement des présences...</p>
        </div>
      </div>
    );
  }

  const todaySessions = sessionsData?.todaySessions || [];
  const pastSessions = sessionsData?.pastSessions || [];
  const futureSessions = sessionsData?.futureSessions || [];
  const hasAnySession = todaySessions.length > 0 || pastSessions.length > 0 || futureSessions.length > 0;

  return (
    <div className="space-y-6">
      {/* A failed statistics request used to leave this area blank. */}
      {statsError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm text-red-700">Erreur lors du chargement des statistiques de présence.</span>
          <button
            type="button"
            onClick={() => void refetchStats()}
            className="text-sm font-medium text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}
      {/* Stats Summary Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-gray-900">{stats.totalSessions}</div>
                <div className="text-sm text-gray-500 mt-1">Séances totales</div>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <CalendarDays className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-emerald-600">{stats.completedSessions}</div>
                <div className="text-sm text-gray-500 mt-1">Séances marquées</div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-purple-600">{stats.totalStudents}</div>
                <div className="text-sm text-gray-500 mt-1">Étudiants</div>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-blue-600">{stats.averageAttendanceRate}%</div>
                <div className="text-sm text-gray-500 mt-1">Taux moyen</div>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setViewMode('sessions')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              viewMode === 'sessions'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Calendar className="h-4 w-4 inline mr-2" />
            Séances
          </button>
          <button
            onClick={() => setViewMode('students')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              viewMode === 'students'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Statistiques
          </button>
        </div>

        {/* Export Button */}
        <div className="relative" ref={exportMenuRef}>
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Exporter
            <ChevronDown className="h-3 w-3" />
          </button>

          {showExportMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
              <button
                onClick={handleExportExcel}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                <span>Excel (.xlsx)</span>
              </button>
              <button
                onClick={handleExportPdf}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FileText className="h-4 w-4 text-red-500" />
                <span>PDF (.pdf)</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* No Sessions Message */}
      {!hasAnySession && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-10 w-10 text-blue-400" />
          </div>
          <h4 className="text-xl font-semibold text-gray-900 mb-2">Aucune séance disponible</h4>
          <p className="text-gray-500 max-w-md mx-auto">
            Les séances sont créées par l'administration dans le module d'emploi du temps.
            Contactez votre coordinateur si vous pensez qu'il y a un problème.
          </p>
        </div>
      )}

      {/* Sessions View */}
      {viewMode === 'sessions' && hasAnySession && (
        <>
          {/* Today's Sessions */}
          {todaySessions.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="h-5 w-5" />
                <span className="font-semibold">
                  {todaySessions.length === 1 ? 'Séance du jour' : `Séances du jour (${todaySessions.length})`}
                </span>
              </div>
              {todaySessions.map((todaySession, index) => (
                <div key={todaySession.id} className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-6 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                          <Clock className="h-5 w-5" />
                        </div>
                        <span className="text-lg font-semibold">
                          {todaySessions.length > 1 ? `Séance ${index + 1}` : 'Séance du jour'}
                        </span>
                      </div>
                      {getAttendanceStatusBadge(todaySession.attendanceStatus)}
                    </div>

                    <h3 className="text-2xl font-bold mb-3">
                      {formatDate(todaySession.date)}
                    </h3>

                    <div className="flex flex-wrap items-center gap-4 text-white/80 mb-5">
                      <span className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {todaySession.startTime} - {todaySession.endTime}
                      </span>
                      {todaySession.roomName && (
                        <span className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {todaySession.roomName}
                        </span>
                      )}
                    </div>

                    {/* Quick stats for today */}
                    {todaySession.attendanceStatus !== 'not_filled' && (
                      <div className="flex items-center gap-6 text-sm mb-5">
                        <span className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                          {todaySession.presentCount} présents
                        </span>
                        <span className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-red-400 rounded-full" />
                          {todaySession.absentUnexcusedCount} absents
                        </span>
                        {todaySession.absentExcusedCount > 0 && (
                          <span className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full" />
                            {todaySession.absentExcusedCount} excusés
                          </span>
                        )}
                        {todaySession.lateCount > 0 && (
                          <span className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-amber-400 rounded-full" />
                            {todaySession.lateCount} retards
                          </span>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => setSelectedSession(todaySession)}
                      className="px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-md"
                    >
                      {todaySession.attendanceStatus === 'not_filled'
                        ? 'Marquer les présences'
                        : 'Modifier les présences'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Future Sessions */}
          {futureSessions.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowFutureSessions(!showFutureSessions)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Lock className="h-4 w-4 text-gray-500" />
                  </div>
                  <span className="font-semibold text-gray-900">
                    Séances à venir ({futureSessions.length})
                  </span>
                </div>
                {showFutureSessions ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {showFutureSessions && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {futureSessions.slice(0, 5).map((session) => (
                    <div
                      key={session.id}
                      className="p-4 opacity-75"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <Lock className="h-4 w-4 text-gray-400" />
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">
                              {formatShortDate(session.date)}
                            </span>
                            <span className="text-sm text-gray-400 ml-3">
                              {session.startTime} - {session.endTime}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          Aperçu uniquement
                        </span>
                      </div>
                    </div>
                  ))}
                  {futureSessions.length > 5 && (
                    <div className="p-3 text-center text-sm text-gray-500">
                      + {futureSessions.length - 5} autres séances
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Past Sessions History */}
          {pastSessions.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  Historique des séances
                </h3>
              </div>

              <div className="divide-y divide-gray-50">
                {pastSessions.map((session) => (
                  <div
                    key={session.id}
                    onClick={() => !session.isCancelled && setSelectedSession(session)}
                    className={`p-4 transition-colors ${
                      session.isCancelled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-blue-50/50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2.5 rounded-xl ${
                          session.attendanceStatus === 'complete'
                            ? 'bg-emerald-100'
                            : session.attendanceStatus === 'partial'
                            ? 'bg-amber-100'
                            : session.isCancelled
                            ? 'bg-gray-100'
                            : 'bg-gray-100'
                        }`}>
                          {session.isCancelled ? (
                            <Ban className="h-4 w-4 text-gray-500" />
                          ) : session.attendanceStatus === 'complete' ? (
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                          ) : session.attendanceStatus === 'partial' ? (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          ) : (
                            <Clock className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">
                            {formatShortDate(session.date)}
                          </span>
                          <span className="text-sm text-gray-500 ml-3">
                            {session.startTime} - {session.endTime}
                          </span>
                          {session.roomName && (
                            <span className="text-sm text-gray-400 ml-2">
                              ({session.roomName})
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {!session.isCancelled && session.attendanceStatus !== 'not_filled' && (
                          <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="h-3.5 w-3.5" />
                              {session.presentCount}
                            </span>
                            <span className="flex items-center gap-1 text-red-600">
                              <XCircle className="h-3.5 w-3.5" />
                              {session.absentUnexcusedCount}
                            </span>
                            {session.lateCount > 0 && (
                              <span className="flex items-center gap-1 text-amber-600">
                                <Clock className="h-3.5 w-3.5" />
                                {session.lateCount}
                              </span>
                            )}
                          </div>
                        )}
                        {getAttendanceStatusBadge(session.attendanceStatus)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Students Statistics View */}
      {/* Group-level rates: the figures a professor reads first, computed over
          every mark of the group rather than averaged per student. */}
      {viewMode === 'students' && stats?.groupRates && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Taux du groupe</h3>
            <span className="text-sm text-gray-500">
              Sur {stats.groupRates.totalMarks} présence(s) enregistrée(s)
            </span>
          </div>

          <div className="space-y-3">
            {[
              {
                label: 'Présence',
                hint: 'présents + retards',
                value: stats.groupRates.presenceRate,
                count: stats.groupRates.totalPresences + stats.groupRates.totalLates,
                bar: 'bg-emerald-500',
                text: 'text-emerald-600',
              },
              {
                label: 'Retards',
                value: stats.groupRates.lateRate,
                count: stats.groupRates.totalLates,
                bar: 'bg-amber-500',
                text: 'text-amber-600',
              },
              {
                label: 'Absences justifiées',
                value: stats.groupRates.absenceExcusedRate,
                count: stats.groupRates.totalAbsencesExcused,
                bar: 'bg-blue-500',
                text: 'text-blue-600',
              },
              {
                label: 'Absences non justifiées',
                value: stats.groupRates.absenceUnexcusedRate,
                count: stats.groupRates.totalAbsencesUnexcused,
                bar: 'bg-red-500',
                text: 'text-red-600',
              },
            ].map((rate) => (
              <div key={rate.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700 font-medium">
                    {rate.label}
                    {rate.hint && <span className="text-gray-400 font-normal"> ({rate.hint})</span>}
                  </span>
                  <span className={`font-semibold ${rate.text}`}>
                    {rate.value}% · {rate.count}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${rate.bar}`}
                    style={{ width: `${Math.min(100, rate.value)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {stats.groupRates.studentsAboveThreshold > 0 && (
            <div className="mt-4 flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertTriangle className="h-4 w-4" />
              {stats.groupRates.studentsAboveThreshold} étudiant(s) au-delà du seuil de{' '}
              {stats.absenceThreshold} absences non justifiées
            </div>
          )}
        </div>
      )}

      {viewMode === 'students' && stats && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                Statistiques par étudiant
              </h3>
              <div className="text-sm text-gray-500">
                Seuil d'alerte: {stats.absenceThreshold} absences non excusées
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Présences
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Absences
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Excusées
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Retards
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Taux
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stats.studentStats.map((student: StudentAttendanceStats) => (
                  <tr
                    key={student.studentId}
                    className={`hover:bg-gray-50 ${
                      student.exceedsAbsenceThreshold ? 'bg-red-50/50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${getAvatarColor(student.studentName)} flex items-center justify-center text-white font-semibold text-sm`}>
                          {getInitials(student.studentName)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{student.studentName}</span>
                            {student.isFormerMember && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full"
                                title="Cet étudiant a quitté le groupe ; ses présences enregistrées ici sont conservées"
                              >
                                <History className="h-3 w-3" />
                                Ancien membre
                              </span>
                            )}
                            {student.exceedsAbsenceThreshold && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                <AlertTriangle className="h-3 w-3" />
                                Alerte
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{student.studentCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg font-semibold text-sm">
                        {student.totalPresences}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 ${
                        student.exceedsAbsenceThreshold ? 'bg-red-100 text-red-700' : 'bg-red-50 text-red-600'
                      } rounded-lg font-semibold text-sm`}>
                        {student.totalAbsencesUnexcused}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm">
                        {student.totalAbsencesExcused}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-lg font-semibold text-sm">
                        {student.totalLates}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              student.attendanceRate >= 80
                                ? 'bg-emerald-500'
                                : student.attendanceRate >= 60
                                ? 'bg-amber-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${student.attendanceRate}%` }}
                          />
                        </div>
                        <span className={`font-semibold text-sm ${
                          student.attendanceRate >= 80
                            ? 'text-emerald-600'
                            : student.attendanceRate >= 60
                            ? 'text-amber-600'
                            : 'text-red-600'
                        }`}>
                          {student.attendanceRate}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {selectedSession && !selectedSession.isFuture && (
        <AttendanceModal
          session={selectedSession}
          groupeId={groupeId}
          moduleElementId={moduleElementId}
          onClose={() => setSelectedSession(null)}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
};

export default AttendanceTab;
